import { EPISODE_COBALT_BLUE } from '@/constants/colors';
import { getEpisodeLabel } from '@/utils/episodeLabel';
import { Box, Typography, useTheme } from '@mui/material';
import { FC, useState } from 'react';

import { formatEpisodeWindow } from '../PatientEpisodeViewHeader';
import { Episode } from './types';
import { getEpisodeEndDate } from './utils';

const TOP_PADDING = 24;
const BOTTOM_PADDING = 24;
const CONTAINER_WIDTH = 60;
const BAR_LEFT = 40;
const TODAY_DOT_SIZE = 10;
const TODAY_DOT_LEFT = 2;
const TODAY_LINE_LEFT = TODAY_DOT_LEFT + TODAY_DOT_SIZE;
const TODAY_LINE_WIDTH = BAR_LEFT - TODAY_LINE_LEFT;
const DOT_SIZE = 12;
const DOT_GAP = 2;
const DOT_BASE_LEFT = 34;
const MIN_Y_SPACING = DOT_SIZE + DOT_GAP;

const getProportionalY = (date: Date, minMs: number, maxMs: number, containerHeight: number) => {
    const usable = containerHeight - TOP_PADDING - BOTTOM_PADDING;
    const ratio = (date.getTime() - minMs) / (maxMs - minMs);
    return TOP_PADDING + ratio * usable;
};

const applyMinSpacing = (sortedYs: number[], minSpacing: number, maxY: number): number[] => {
    const ys = [...sortedYs];
    // Forward pass: push dots down to enforce minimum spacing.
    for (let i = 1; i < ys.length; i++) {
        if (ys[i] - ys[i - 1] < minSpacing) {
            ys[i] = ys[i - 1] + minSpacing;
        }
    }
    if (ys[ys.length - 1] > maxY) {
        // Backward pass: pull dots up when the last dot overflows maxY.
        ys[ys.length - 1] = maxY;
        for (let i = ys.length - 2; i >= 0; i--) {
            if (ys[i + 1] - ys[i] < minSpacing) {
                ys[i] = ys[i + 1] - minSpacing;
            }
            ys[i] = Math.max(TOP_PADDING, ys[i]);
        }
        // Second forward pass: TOP_PADDING clamps in the backward pass can
        // violate minSpacing for dots near the top. Re-enforce spacing so
        // clustered dots don't overlap (they may spill past maxY, which is
        // preferable to visual overlap).
        for (let i = 1; i < ys.length; i++) {
            if (ys[i] - ys[i - 1] < minSpacing) {
                ys[i] = ys[i - 1] + minSpacing;
            }
        }
    }
    return ys;
};

type EpisodeDotsProps = {
    allEpisodes: Episode[];
    minMs: number;
    maxMs: number;
    containerHeight: number;
    activeTabEpisodeId?: string | null;
    onEpisodeClick: (episodeId: string) => void;
};

const EpisodeDots: FC<EpisodeDotsProps> = ({
    allEpisodes,
    minMs,
    maxMs,
    containerHeight,
    activeTabEpisodeId,
    onEpisodeClick,
}) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    // Group episodes by start date string key (avoids float-equality collisions)
    const rawYMap = new Map<string, { y: number; eps: typeof allEpisodes }>();
    for (const ep of allEpisodes) {
        const key = ep.startDate?.toISOString() ?? 'null';
        const y = ep.startDate
            ? getProportionalY(ep.startDate, minMs, maxMs, containerHeight)
            : containerHeight / 2;
        const existing = rawYMap.get(key);
        if (existing) {
            existing.eps.push(ep);
        } else {
            rawYMap.set(key, { y, eps: [ep] });
        }
    }

    // Apply minimum Y spacing across groups
    const sortedEntries = Array.from(rawYMap.values()).sort((a, b) => a.y - b.y);
    const adjustedYs = applyMinSpacing(
        sortedEntries.map((e) => e.y),
        MIN_Y_SPACING,
        containerHeight - BOTTOM_PADDING,
    );

    const episodesWithXY = sortedEntries.flatMap((entry, i) =>
        entry.eps.map((ep, xIndex) => ({
            ep,
            yOffset: adjustedYs[i],
            xIndex,
        })),
    );

    return (
        <>
            {episodesWithXY.map(({ ep, yOffset, xIndex }) => {
                const xLeft = DOT_BASE_LEFT + xIndex * (DOT_SIZE + DOT_GAP);
                const isHovered = hoveredId === ep.id;
                const isActive = activeTabEpisodeId === ep.id;
                const title = getEpisodeLabel(ep.typeLabel, ep.numberInSequence);
                const dateRange =
                    ep.startDate && ep.endDate ? formatEpisodeWindow(ep.startDate, ep.endDate) : '';

                return (
                    <Box
                        key={ep.id}
                        position='absolute'
                        top={`${yOffset}px`}
                        left={`${xLeft}px`}
                        sx={{
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            width: isHovered ? 'fit-content' : '12px',
                            height: isHovered ? 'auto' : '12px',
                            minHeight: '12px',
                            borderRadius: isHovered ? '8px' : '50%',
                            bgcolor: EPISODE_COBALT_BLUE,
                            transition: 'width 0.15s ease, border-radius 0.07s ease',
                            overflow: isHovered ? 'visible' : 'hidden',
                            outline: isActive ? '2px solid #EC4899' : 'none',
                            outlineOffset: '2px',
                            zIndex: isHovered ? 10 : 1,
                            color: '#FFFFFF',
                        }}
                        onMouseEnter={() => setHoveredId(ep.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        onClick={() => onEpisodeClick(ep.id)}
                    >
                        <Box
                            px={1}
                            py={0.5}
                            sx={{
                                opacity: isHovered ? 1 : 0,
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                            }}
                        >
                            <Typography
                                variant='caption'
                                fontWeight={600}
                                display='block'
                                lineHeight={1.3}
                            >
                                {title}
                            </Typography>
                            {dateRange && (
                                <Typography
                                    variant='caption'
                                    display='block'
                                    lineHeight={1.3}
                                    fontSize='10px'
                                >
                                    {dateRange}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </>
    );
};

type EpisodeTimelineProps = {
    allEpisodes: Episode[];
    containerHeight: number;
    activeTabEpisodeId?: string | null;
    onEpisodeClick: (episodeId: string) => void;
};

export const EpisodeTimeline: FC<EpisodeTimelineProps> = ({
    allEpisodes,
    containerHeight,
    activeTabEpisodeId,
    onEpisodeClick,
}) => {
    const theme = useTheme();

    const minMs = Math.min(...allEpisodes.map((ep) => ep.startDate?.getTime() ?? Infinity));
    const maxMs = Math.max(...allEpisodes.map((ep) => getEpisodeEndDate(ep).getTime()));

    if (!isFinite(minMs) || !isFinite(maxMs) || minMs === maxMs) {
        return null;
    }

    const todayMs = Date.now();
    const showToday = todayMs >= minMs && todayMs <= maxMs;
    const todayY = showToday ? getProportionalY(new Date(), minMs, maxMs, containerHeight) : null;

    return (
        <Box
            position='relative'
            width={`${CONTAINER_WIDTH}px`}
            flexShrink={0}
            height={`${containerHeight}px`}
            overflow='visible'
        >
            <Box
                position='absolute'
                left={`${BAR_LEFT}px`}
                top={`${TOP_PADDING}px`}
                bottom={`${BOTTOM_PADDING}px`}
                width='2px'
                bgcolor='#E5E7EB'
            />

            {todayY !== null && (
                <>
                    <Typography
                        position='absolute'
                        left={`${TODAY_LINE_LEFT}px`}
                        top={`${todayY - 1}px`}
                        sx={{
                            transform: 'translateY(-100%)',
                            fontSize: '9px',
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            zIndex: 2,
                        }}
                    >
                        Today
                    </Typography>
                    <Box
                        position='absolute'
                        left={`${TODAY_LINE_LEFT}px`}
                        top={`${todayY}px`}
                        sx={{
                            transform: 'translateY(-50%)',
                            width: `${TODAY_LINE_WIDTH}px`,
                            height: '2px',
                            bgcolor: theme.palette.primary.main,
                            zIndex: 2,
                        }}
                    />
                    <Box
                        component='div'
                        title={`Today: ${new Date().toLocaleDateString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                        })}`}
                        position='absolute'
                        left={`${TODAY_DOT_LEFT}px`}
                        top={`${todayY}px`}
                        sx={{
                            transform: 'translateY(-50%)',
                            width: `${TODAY_DOT_SIZE}px`,
                            height: `${TODAY_DOT_SIZE}px`,
                            borderRadius: '50%',
                            bgcolor: theme.palette.primary.main,
                            zIndex: 2,
                        }}
                    />
                </>
            )}

            <EpisodeDots
                allEpisodes={allEpisodes}
                minMs={minMs}
                maxMs={maxMs}
                containerHeight={containerHeight}
                activeTabEpisodeId={activeTabEpisodeId}
                onEpisodeClick={onEpisodeClick}
            />
        </Box>
    );
};
